import { useState, useEffect } from 'react'
import { CheckIcon, PlusIcon, TrashIcon, UserIcon, XIcon } from '../icons'
import type { KanbanBoard, PublicUser, Workspace, WorkspaceMember } from '../types'
import { API_URL, getAuthHeaders } from '../api'
import { useAuth } from '../context/AuthContext'
import { avatarUrl as getAvatarUrl } from '../utils/avatar'

export function EditWorkspaceModal({ workspace, allUsers, onClose, onUpdate }: { workspace: Workspace; allUsers: PublicUser[]; onClose: () => void; onUpdate: (b?: KanbanBoard) => void }) {
  const { user: currentUser } = useAuth()
  const [name, setName] = useState(workspace.name || '')
  const [members, setMembers] = useState<WorkspaceMember[]>([])
  const [allUsersList, setAllUsersList] = useState<PublicUser[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const loadMembers = () => {
    fetch(`${API_URL}/api/workspaces/${workspace.id}/members`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => setMembers(data.members || []))
      .catch(() => {})
  }

  useEffect(() => {
    loadMembers()
    fetch(`${API_URL}/api/users`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => setAllUsersList(data.users || []))
      .catch(() => {})
  }, [workspace.id])

  const handleSaveName = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/api/workspaces/${workspace.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: name.trim() }),
      })
      if (res.ok) {
        onUpdate()
      } else {
        const data = await res.json()
        setError(data.error || 'Erro ao salvar')
      }
    } catch {
      setError('Erro de conexão')
    }
    setSaving(false)
  }

  const handleAddMember = async (userId: string) => {
    try {
      await fetch(`${API_URL}/api/workspaces/${workspace.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ user_id: userId, role: 'member' }),
      })
      loadMembers()
    } catch { /* ignore */ }
  }

  const handleRemoveMember = async (userId: string) => {
    try {
      await fetch(`${API_URL}/api/workspaces/${workspace.id}/members`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ user_id: userId }),
      })
      loadMembers()
    } catch { /* ignore */ }
  }

  const handleDeleteWorkspace = async () => {
    if (!confirm(`Deletar "${workspace.name}"?\n\nTudo será removido: canais, membros, boards, tarefas e mensagens.\n\nEsta ação NÃO pode ser desfeita.`)) return
    try {
      await fetch(`${API_URL}/api/workspaces/${workspace.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
      onClose()
      onUpdate()
      window.location.href = '/'
    } catch { /* ignore */ }
  }

  const memberIds = new Set(members.map(m => m.id))
  const nonMembers = allUsersList.filter(u => !memberIds.has(u.id))

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Workspace</h2>
          <button className="icon-btn" onClick={onClose}><XIcon size={18} /></button>
        </div>

        <div className="profile-modal-form">
          {/* Name */}
          <div className="profile-field">
            <label>Nome do workspace</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Nome do workspace"
                autoFocus
              />
              <button type="button" className="btn-primary" onClick={handleSaveName} disabled={saving || !name.trim()}>
                <CheckIcon size={16} /> Salvar
              </button>
            </div>
          </div>

          {/* Members */}
          <div className="profile-field">
            <label>Membros ({members.length})</label>
            <div className="ws-members-list">
              {members.map(m => (
                <div key={m.id} className="ws-member-row">
                  {m.avatar_url ? (
                    <img src={getAvatarUrl(m)} alt="" className="avatar-img" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                  ) : (
                    <span className="ws-member-avatar"><UserIcon size={14} /></span>
                  )}
                  <span className="ws-member-name">{m.display_name || m.name || m.email}</span>
                  {m.id !== workspace.owner_id && (
                    <button className="ws-member-remove" onClick={() => handleRemoveMember(m.id!)} title="Remover">
                      <XIcon size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add members */}
          {nonMembers.length > 0 && (
            <div className="profile-field">
              <label>Adicionar membros</label>
              <div className="ws-members-list">
                {nonMembers.map(u => (
                  <div key={u.id} className="ws-member-row ws-member-add">
                    {u.avatar_url ? (
                      <img src={getAvatarUrl(u)} alt="" className="avatar-img" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                    ) : (
                      <span className="ws-member-avatar"><UserIcon size={14} /></span>
                    )}
                    <span className="ws-member-name">{u.display_name || u.name || u.email}</span>
                    <button className="ws-member-add-btn" onClick={() => handleAddMember(u.id)} title="Adicionar">
                      <PlusIcon size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <div className="profile-error">{error}</div>}

          {/* Danger zone */}
          {workspace.owner_id === currentUser?.id && (
            <div className="danger-zone" style={{ marginTop: 16 }}>
              <div className="danger-zone-row">
                <div>
                  <strong>Excluir workspace</strong>
                  <p>Remove permanentemente tudo: canais, membros, boards, tarefas e mensagens.</p>
                </div>
                <button type="button" className="btn-danger" onClick={handleDeleteWorkspace}>
                  <TrashIcon size={14} /> Excluir
                </button>
              </div>
            </div>
          )}

          <div className="profile-modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  )
}
